import { ArrowRight, CalendarPlus, Keyboard, Loader2, Search, Video, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MeetingCard, meetRoomPath } from '../components/meet/MeetingCard';
import {
  ScheduleMeetingModal,
  type MeetingFormInitial,
} from '../components/meet/ScheduleMeetingModal';
import { useTestMode } from '../components/testMode/TestModeContext';
import { useConfirm, usePrompt } from '../components/ui/ConfirmProvider';
import { useI18n } from '../contexts/I18nContext';
import {
  useCancelMeeting,
  useCreateMeeting,
  useEndMeeting,
  useMeeting,
  useMeetings,
  useUpdateMeeting,
} from '../hooks/useMeet';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../store/useAuthStore';
import { useActiveCompanyId } from '../store/useCompanyStore';
import type { VideoMeetingSummary } from '../lib/meetApi';

type Tab = 'upcoming' | 'past';

/**
 * A meeting code out of whatever was pasted: the bare code, a full join link,
 * or a link with a query string after it. Anything that is not a plausible
 * code comes back null, so the button stays inert rather than opening a 404.
 */
function codeFromInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withoutQuery = trimmed.split(/[?#]/)[0];
  const last = withoutQuery.split('/').filter(Boolean).pop() ?? '';
  const code = last.trim();
  return /^[A-Za-z0-9_-]{4,64}$/.test(code) ? code : null;
}

/** Calendar-day bucket, so meetings on the same day sit under one heading. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function groupByDay(
  meetings: VideoMeetingSummary[],
): { key: string; date: Date; items: VideoMeetingSummary[] }[] {
  const groups: { key: string; date: Date; items: VideoMeetingSummary[] }[] = [];
  for (const m of meetings) {
    const key = dayKey(m.scheduledAt);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(m);
    else groups.push({ key, date: new Date(m.scheduledAt), items: [m] });
  }
  return groups;
}

const dayFormat = new Intl.DateTimeFormat('ro-RO', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/**
 * The stack of paper an empty list deserves: a drawn placeholder instead of a
 * grey sentence, so the page still looks finished on the first day.
 */
function EmptyIllustration() {
  return (
    <svg
      viewBox="0 0 160 110"
      role="img"
      aria-hidden
      className="h-28 w-40 text-muted-foreground"
      fill="none"
    >
      <rect
        x="14"
        y="22"
        width="102"
        height="66"
        rx="10"
        className="fill-accent"
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth="2"
      />
      <path
        d="M116 47l24-14v44l-24-14z"
        className="fill-emerald-500/25"
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="52" cy="50" r="10" className="fill-emerald-500/50" />
      <path
        d="M38 74c2.6-7 7.4-10.5 14-10.5S63.4 67 66 74"
        stroke="currentColor"
        strokeOpacity="0.45"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <rect x="80" y="44" width="26" height="4" rx="2" className="fill-current" opacity="0.28" />
      <rect x="80" y="54" width="20" height="4" rx="2" className="fill-current" opacity="0.2" />
      <rect x="80" y="64" width="24" height="4" rx="2" className="fill-current" opacity="0.2" />
    </svg>
  );
}

/**
 * /meet — Firmio Meet's front page: start a meeting now, schedule one, join
 * one from a pasted link, and see what is coming up and what already happened
 * (with its transcript).
 */
export function MeetPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const me = useAuthStore((s) => s.user);
  const { can } = usePermissions();
  const { enabled: testMode } = useTestMode();
  const companyId = useActiveCompanyId();
  const [tab, setTab] = useState<Tab>('upcoming');
  const [scheduling, setScheduling] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [joinInput, setJoinInput] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // The server searches the whole history, not just the 100 rows on screen,
  // so the query waits for a pause in typing instead of firing per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);

  const { data, isLoading, isFetching } = useMeetings({
    tab,
    limit: 100,
    q: debouncedSearch || undefined,
    companyId,
  });
  const create = useCreateMeeting();
  const update = useUpdateMeeting();
  const cancel = useCancelMeeting();
  const end = useEndMeeting();
  // The list carries no guest list, so editing one fetches the detail first;
  // the form opens only once it is here, already filled in.
  const { data: editing } = useMeeting(editingId ?? undefined);
  const canCreate = can('videoMeetings', 'canCreate');
  const canManage = can('videoMeetings', 'canManage');

  const meetings = useMemo(() => data?.meetings ?? [], [data]);
  const groups = useMemo(() => groupByDay(meetings), [meetings]);
  const joinCode = codeFromInput(joinInput);

  const editInitial: MeetingFormInitial | null = useMemo(() => {
    if (!editing || editing.id !== editingId) return null;
    return {
      title: editing.title,
      scheduledAt: editing.scheduledAt,
      durationMinutes: editing.durationMinutes,
      inviteUserIds: editing.invites.map((i) => i.userId).filter((u): u is string => Boolean(u)),
      inviteEmails: editing.invites.map((i) => i.email).filter((e): e is string => Boolean(e)),
      clientId: editing.clientId,
      projectId: editing.projectId,
    };
  }, [editing, editingId]);

  /**
   * An instant meeting still gets a name first: the name is what the
   * history, the meetings log and the invitation show, and "Întâlnire cu
   * Andrei" told nobody what it was about. Left empty, the server's default
   * applies; Escape means "never mind".
   */
  const startNow = async () => {
    if (testMode) return;
    const title = await prompt({
      title: t('meet.instant'),
      message: t('meet.instantNamePrompt'),
      placeholder: t('meet.form.titlePlaceholder'),
      confirmLabel: t('meet.instantStart'),
    });
    if (title === null) return;
    create.mutate(
      { companyId, title: title.trim() || undefined },
      { onSuccess: (meeting) => navigate(meetRoomPath(meeting.code)) },
    );
  };

  const onCancel = async (id: string) => {
    const yes = await confirm({
      title: t('meet.cancel'),
      message: t('meet.confirmCancel'),
      confirmLabel: t('meet.cancel'),
      danger: true,
    });
    if (yes) cancel.mutate(id);
  };

  const onEnd = async (id: string) => {
    const yes = await confirm({
      title: t('meet.controls.endForAll'),
      message: t('meet.controls.confirmEndForAll'),
      confirmLabel: t('meet.controls.endForAll'),
      danger: true,
    });
    if (yes) end.mutate(id);
  };

  /** "Azi" / "Mâine" / "Ieri" read faster than the date they stand for. */
  const dayLabel = (date: Date): string => {
    const today = new Date();
    const diff = Math.round(
      (new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() -
        new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()) /
        86_400_000,
    );
    if (diff === 0) return t('meet.day.today');
    if (diff === 1) return t('meet.day.tomorrow');
    if (diff === -1) return t('meet.day.yesterday');
    return dayFormat.format(date);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
        <Video size={18} className="shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-heading text-base font-semibold text-foreground">
            {t('meet.title')}
          </h1>
          <p className="text-xs text-muted-foreground">
            {data?.transcriptionEnabled ? t('meet.subtitle') : t('meet.transcript.off')}
          </p>
        </div>
        {canCreate && (
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => setScheduling(true)}
              disabled={testMode}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50"
            >
              <CalendarPlus size={16} />
              <span className="hidden sm:inline">{t('meet.schedule')}</span>
            </button>
            <button
              type="button"
              onClick={() => void startNow()}
              disabled={testMode || create.isPending}
              title={testMode ? t('meet.testMode') : undefined}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-fixed-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {create.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Video size={16} />
              )}
              {t('meet.instant')}
            </button>
          </div>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
          {/* The front door: what this is, how to start one, how to join one. */}
          <section className="rounded-2xl border border-border bg-card px-5 py-6 shadow-sm sm:px-7 sm:py-7">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
              <div className="min-w-0 flex-1">
                <h2 className="font-heading text-xl font-semibold text-foreground sm:text-2xl">
                  {t('meet.hero.title')}
                </h2>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
                  {t('meet.hero.subtitle')}
                </p>
                {canCreate && (
                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void startNow()}
                      disabled={testMode || create.isPending}
                      title={testMode ? t('meet.testMode') : undefined}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-fixed-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {create.isPending ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Video size={16} />
                      )}
                      {t('meet.instant')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setScheduling(true)}
                      disabled={testMode}
                      className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent disabled:opacity-50"
                    >
                      <CalendarPlus size={16} />
                      {t('meet.schedule')}
                    </button>
                  </div>
                )}
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (joinCode) navigate(meetRoomPath(joinCode));
                }}
                className="w-full lg:w-72"
              >
                <div className="flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 focus-within:border-emerald-500/60">
                  <Keyboard size={16} className="shrink-0 text-muted-foreground" />
                  <input
                    value={joinInput}
                    onChange={(e) => setJoinInput(e.target.value)}
                    placeholder={t('meet.joinPlaceholder')}
                    aria-label={t('meet.joinPlaceholder')}
                    className="min-w-0 flex-1 bg-transparent py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={!joinCode}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-sm font-semibold text-emerald-500 transition-colors hover:bg-emerald-500/10 disabled:text-muted-foreground disabled:hover:bg-transparent"
                  >
                    {t('meet.joinAction')}
                    <ArrowRight size={14} />
                  </button>
                </div>
                <p className="mt-2 px-1 text-xs text-muted-foreground">{t('meet.joinHint')}</p>
              </form>
            </div>
          </section>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex gap-1.5">
              {(['upcoming', 'past'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={[
                    'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
                    tab === key
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  ].join(' ')}
                >
                  {t(`meet.tabs.${key}`)}
                </button>
              ))}
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 focus-within:border-emerald-500/60 sm:ml-auto sm:max-w-sm">
              {isFetching && debouncedSearch ? (
                <Loader2 size={16} className="shrink-0 animate-spin text-muted-foreground" />
              ) : (
                <Search size={16} className="shrink-0 text-muted-foreground" />
              )}
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setSearch('');
                }}
                placeholder={t('meet.search.placeholder')}
                aria-label={t('meet.search.placeholder')}
                className="min-w-0 flex-1 bg-transparent py-0.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none [&::-webkit-search-cancel-button]:hidden"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label={t('meet.search.clear')}
                  title={t('meet.search.clear')}
                  className="shrink-0 rounded-full p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="mt-4">
            {isLoading ? (
              <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 size={14} className="animate-spin" />
                {t('common.loading')}
              </p>
            ) : meetings.length === 0 && debouncedSearch ? (
              <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border px-6 py-10 text-center">
                <Search size={22} className="text-muted-foreground" />
                <h3 className="font-heading text-base font-semibold text-foreground">
                  {t('meet.search.emptyTitle', { q: debouncedSearch })}
                </h3>
                <p className="max-w-sm text-sm text-muted-foreground">
                  {t('meet.search.emptyHint')}
                </p>
              </div>
            ) : meetings.length === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border px-6 py-12 text-center">
                <EmptyIllustration />
                <h3 className="font-heading text-base font-semibold text-foreground">
                  {tab === 'upcoming' ? t('meet.empty.upcomingTitle') : t('meet.empty.pastTitle')}
                </h3>
                <p className="max-w-sm text-sm text-muted-foreground">
                  {tab === 'upcoming' ? t('meet.empty.upcomingHint') : t('meet.empty.pastHint')}
                </p>
                {tab === 'upcoming' && canCreate && (
                  <button
                    type="button"
                    onClick={() => void startNow()}
                    disabled={testMode || create.isPending}
                    className="mt-1 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-fixed-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <Video size={16} />
                    {t('meet.instant')}
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {groups.map((group) => (
                  <section key={group.key}>
                    <h3 className="mb-2 px-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                      {dayLabel(group.date)}
                    </h3>
                    <ul className="space-y-2">
                      {group.items.map((m) => (
                        <MeetingCard
                          key={m.id}
                          meeting={m}
                          isHost={canManage || m.hostId === me?.id}
                          onEdit={(target) => setEditingId(target.id)}
                          onCancel={(id) => void onCancel(id)}
                          onEnd={(id) => void onEnd(id)}
                        />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {scheduling && (
        <ScheduleMeetingModal
          submitting={create.isPending}
          onClose={() => setScheduling(false)}
          onSubmit={async (input) => {
            await create.mutateAsync({ ...input, companyId });
            setScheduling(false);
            setTab('upcoming');
          }}
        />
      )}

      {editingId && editInitial && editing && (
        <ScheduleMeetingModal
          key={editingId}
          initial={editInitial}
          canReschedule={editing.status === 'SCHEDULED'}
          hostIsMe={editing.hostId === me?.id}
          submitting={update.isPending}
          onClose={() => setEditingId(null)}
          onSubmit={async (input) => {
            await update.mutateAsync({
              id: editingId,
              title: input.title,
              // A meeting already running keeps its slot; the form disabled it.
              ...(editing.status === 'SCHEDULED'
                ? { scheduledAt: input.scheduledAt, durationMinutes: input.durationMinutes }
                : {}),
              inviteUserIds: input.inviteUserIds,
              inviteEmails: input.inviteEmails,
              clientId: input.clientId,
              projectId: input.projectId,
            });
            setEditingId(null);
          }}
        />
      )}
    </div>
  );
}
